import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Box,
    Container,
    Flex,
    Text,
    Spinner,
    Center,
    HStack,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    Select,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    VStack,
    Checkbox,
    Tag,
    TagLabel,
    TagCloseButton,
} from '@chakra-ui/react';
import { ChevronRightIcon, TriangleUpIcon, TriangleDownIcon } from '@chakra-ui/icons';
import PageTitle from '../components/PageTitle';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useSimStats } from '../hooks/useSimStats';

type StatType = 'batting' | 'pitching';

type FilterChip =
    | { type: 'name'; label: string }
    | { type: 'period'; label: string }
    | { type: 'team'; label: string }
    | { type: 'position'; label: string; value: string }
    | { type: 'minAb'; label: string }
    | { type: 'minIp'; label: string };

const StatisticsPage: React.FC = () => {
    const { myLeague } = useMyLeagueStore();
    const leagueId = myLeague?.id ? String(myLeague.id) : undefined;

    const [statType, setStatType] = useState<StatType>('batting');
    const [sortField, setSortField] = useState<string>('player');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [periodFilter, setPeriodFilter] = useState<'ytd' | '7_days'>('ytd');
    const [nameFilter, setNameFilter] = useState('');
    const [teamFilter, setTeamFilter] = useState<string>('');
    const [positionFilter, setPositionFilter] = useState<string[]>([]);
    const [minAb, setMinAb] = useState<string>('');
    const [minIp, setMinIp] = useState<string>('');

    const teamOptions = useMemo(() => {
        const league = myLeague;
        if (!league) return [] as { id: string; label: string }[];
        const teams: { id: string; label: string }[] = [];
        for (const conf of league.conferences || []) {
            for (const div of conf.divisions || []) {
                for (const team of div.teams || []) {
                    const id = String(team.id);
                    const abbrev = team.abbreviation || '';
                    const name = team.name || '';
                    const label = abbrev && name ? `${abbrev} - ${name}` : abbrev || name || id;
                    teams.push({ id, label });
                }
            }
        }
        return teams;
    }, [myLeague]);

    const filters = useMemo(() => {
        const leagueSeasonYear =
            typeof myLeague?.year === 'number' && Number.isFinite(myLeague.year)
                ? myLeague.year
                : new Date().getFullYear();

        const base: any = {
            // period is enforced as 'ytd' in the backend when not provided,
            // but we include it here to make it explicit and user-selectable.
            period: periodFilter,
            // Backend keys position filter + stats row on `season` (rw_player_positions buckets)
            season: leagueSeasonYear,
        };

        if (nameFilter.trim()) {
            base.name = nameFilter.trim();
        }

        if (teamFilter) {
            base.teamId = teamFilter;
        }

        if (statType === 'batting') {
            if (positionFilter.length > 0) {
                base.positions = positionFilter;
            }
            if (minAb) {
                const v = Number(minAb);
                if (!Number.isNaN(v) && v > 0) base.minAb = v;
            }
        } else {
            if (minIp) {
                const v = Number(minIp);
                if (!Number.isNaN(v) && v > 0) base.minIp = v;
            }
        }

        return base;
    }, [periodFilter, nameFilter, teamFilter, positionFilter, minAb, minIp, statType, myLeague?.year]);

    const statsQuery = useSimStats<any>({
        leagueId,
        type: statType,
        filters,
        page: 1,
        pageSize: 100,
        sortBy: sortField === 'player' ? undefined : { field: sortField, direction: sortDirection },
        enabled: !!leagueId,
    });

    const { data, isLoading, error } = statsQuery;

    const rows = useMemo(() => {
        const base = data?.rows || [];
        if (sortField !== 'player') {
            return base;
        }
        // Client-side sort by last name, then first name when sorting by player
        return [...base].sort((a: any, b: any) => {
            const aP = a.rosterPlayer?.rwPlayer || {};
            const bP = b.rosterPlayer?.rwPlayer || {};
            const aLast = (aP.last_name || '').toLowerCase();
            const bLast = (bP.last_name || '').toLowerCase();
            if (aLast < bLast) return sortDirection === 'ASC' ? -1 : 1;
            if (aLast > bLast) return sortDirection === 'ASC' ? 1 : -1;
            const aFirst = (aP.first_name || '').toLowerCase();
            const bFirst = (bP.first_name || '').toLowerCase();
            if (aFirst < bFirst) return sortDirection === 'ASC' ? -1 : 1;
            if (aFirst > bFirst) return sortDirection === 'ASC' ? 1 : -1;
            return 0;
        });
    }, [data?.rows, sortField, sortDirection]);

    const getPlayerName = (row: any) => {
        const rp = row.rosterPlayer;
        const p = rp?.rwPlayer;
        if (p?.first_name || p?.last_name) {
            return `${p.first_name || ''} ${p.last_name || ''}`.trim();
        }
        return p?.full_name || `#${rp?.id ?? row.roster_player_id}`;
    };

    const getTeamLabel = (row: any) => {
        const teamId = row.rosterPlayer?.team_id;
        if (!teamId) return 'FA';
        const league = myLeague;
        if (!league) return `T${teamId}`;
        for (const conf of league.conferences || []) {
            for (const div of conf.divisions || []) {
                const team = (div.teams || []).find((t: any) => String(t.id) === String(teamId));
                if (team) {
                    // Prefer abbreviation only, fall back to name or raw id
                    return team.abbreviation || team.name || `T${teamId}`;
                }
            }
        }
        return `T${teamId}`;
    };

    const toggleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
        } else {
            setSortField(field);
            // Default new column sorts to DESC (high → low)
            setSortDirection('DESC');
        }
    };

    const togglePosition = (pos: string) => {
        setPositionFilter(prev =>
            prev.includes(pos) ? prev.filter(p => p !== pos) : [...prev, pos]
        );
    };

    const positionOptions = ['CA', '1B', '2B', '3B', 'SS', 'INF', 'LF', 'CF', 'RF', 'OF', 'DH'];

    const renderSortableHeader = (field: string, label: string, isNumeric: boolean = false) => {
        const isActive = sortField === field;
        const icon =
            isActive && sortDirection === 'ASC'
                ? <TriangleUpIcon ml={1} boxSize={3} />
                : isActive && sortDirection === 'DESC'
                    ? <TriangleDownIcon ml={1} boxSize={3} />
                    : null;

        return (
            <Th
                color={isActive ? 'blue.300' : 'white'}
                isNumeric={isNumeric}
                cursor="pointer"
                onClick={() => toggleSort(field)}
            >
                <HStack spacing={1} justify={isNumeric ? 'flex-end' : 'flex-start'}>
                    <Text as="span">{label}</Text>
                    {icon}
                </HStack>
            </Th>
        );
    };

    const chips: FilterChip[] = useMemo(() => {
        const list: FilterChip[] = [];
        if (periodFilter !== 'ytd') {
            list.push({
                type: 'period',
                label: periodFilter === '7_days' ? 'Period: Last 7 Days' : `Period: ${periodFilter}`,
            });
        }
        if (nameFilter.trim()) {
            list.push({ type: 'name', label: `Name: ${nameFilter.trim()}` });
        }
        if (teamFilter && teamOptions.length > 0) {
            const match = teamOptions.find((t) => t.id === teamFilter);
            list.push({
                type: 'team',
                label: `Team: ${match ? match.label : teamFilter}`,
            });
        }
        if (statType === 'batting') {
            positionFilter.forEach((pos) =>
                list.push({ type: 'position', label: pos, value: pos })
            );
            if (minAb) {
                list.push({ type: 'minAb', label: `Min AB: ${minAb}` });
            }
        } else {
            if (minIp) {
                list.push({ type: 'minIp', label: `Min IP: ${minIp}` });
            }
        }
        return list;
    }, [periodFilter, nameFilter, teamFilter, teamOptions, positionFilter, minAb, minIp, statType]);

    const removeChip = (chip: FilterChip) => {
        if (chip.type === 'period') {
            setPeriodFilter('ytd');
        } else if (chip.type === 'name') {
            setNameFilter('');
        } else if (chip.type === 'team') {
            setTeamFilter('');
        } else if (chip.type === 'position') {
            setPositionFilter((prev) => prev.filter((p) => p !== chip.value));
        } else if (chip.type === 'minAb') {
            setMinAb('');
        } else if (chip.type === 'minIp') {
            setMinIp('');
        }
    };

    return (
        <Container maxW="container.xl" py={8}>
            <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} mb={6}>
                <BreadcrumbItem>
                    <BreadcrumbLink as={Link} to="/leagues" color="blue.500">
                        League Management
                    </BreadcrumbLink>
                </BreadcrumbItem>
                {myLeague && (
                    <BreadcrumbItem>
                        <BreadcrumbLink as={Link} to={`/league-details/${myLeague.id}`} color="blue.500">
                            {myLeague.name} League
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                )}
                <BreadcrumbItem isCurrentPage>
                    <Text color="gray.500">League Stats</Text>
                </BreadcrumbItem>
            </Breadcrumb>

            <Flex align="center" justify="space-between" mb={4}>
                <PageTitle>League Stats</PageTitle>
            </Flex>

            {/* Stat type and filters */}
            <VStack align="flex-start" spacing={3} mb={4}>
                <HStack spacing={4} flexWrap="wrap" align="center">
                    {/* Stat type toggle */}
                    <HStack spacing={2}>
                        <Button
                            size="sm"
                            colorScheme={statType === 'batting' ? 'blue' : 'gray'}
                            variant={statType === 'batting' ? 'solid' : 'outline'}
                            onClick={() => setStatType('batting')}
                        >
                            Batters
                        </Button>
                        <Button
                            size="sm"
                            colorScheme={statType === 'pitching' ? 'blue' : 'gray'}
                            variant={statType === 'pitching' ? 'solid' : 'outline'}
                            onClick={() => setStatType('pitching')}
                        >
                            Pitchers
                        </Button>
                    </HStack>

                    {/* Name filter */}
                    <Input
                        placeholder="Filter by name"
                        value={nameFilter}
                        onChange={e => setNameFilter(e.target.value)}
                        width="200px"
                        bg="whiteAlpha.100"
                        color="white"
                        _placeholder={{ color: 'gray.400' }}
                    />

                    {/* Period filter */}
                    <Select
                        value={periodFilter}
                        onChange={e => setPeriodFilter(e.target.value === '7_days' ? '7_days' : 'ytd')}
                        width="170px"
                        bg="whiteAlpha.100"
                        color="white"
                        _placeholder={{ color: 'gray.400' }}
                    >
                        <option value="ytd">YTD</option>
                        <option value="7_days">Last 7 Days</option>
                    </Select>

                    {/* Team filter */}
                    {teamOptions.length > 0 && (
                        <Select
                            placeholder="All teams"
                            value={teamFilter}
                            onChange={e => setTeamFilter(e.target.value)}
                            width="220px"
                            bg="whiteAlpha.100"
                            color="white"
                            _placeholder={{ color: 'gray.400' }}
                        >
                            {teamOptions.map((team) => (
                                <option key={team.id} value={team.id}>
                                    {team.label}
                                </option>
                            ))}
                        </Select>
                    )}

                    {statType === 'batting' && (
                        <>
                            {/* Position multi-select (similar to PlayerSearch) */}
                            <Popover placement="bottom-start">
                                <PopoverTrigger>
                                    <Button variant="outline" colorScheme="blue">
                                        By Position
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent bg="gray.800" color="white" minW="200px">
                                    <PopoverArrow />
                                    <PopoverCloseButton />
                                    <PopoverBody>
                                        <VStack align="flex-start" spacing={1}>
                                            {positionOptions.map(pos => (
                                                <Checkbox
                                                    key={pos}
                                                    isChecked={positionFilter.includes(pos)}
                                                    onChange={() => togglePosition(pos)}
                                                    colorScheme="blue"
                                                >
                                                    {pos}
                                                </Checkbox>
                                            ))}
                                        </VStack>
                                    </PopoverBody>
                                </PopoverContent>
                            </Popover>

                            {/* Min AB */}
                            <Input
                                type="number"
                                min={0}
                                placeholder="Min AB"
                                value={minAb}
                                onChange={e => setMinAb(e.target.value)}
                                width="120px"
                                bg="whiteAlpha.100"
                                color="white"
                                _placeholder={{ color: 'gray.400' }}
                            />
                        </>
                    )}

                    {statType === 'pitching' && (
                        <Input
                            type="number"
                            min={0}
                            step="0.1"
                            placeholder="Min IP"
                            value={minIp}
                            onChange={e => setMinIp(e.target.value)}
                            width="120px"
                            bg="whiteAlpha.100"
                            color="white"
                            _placeholder={{ color: 'gray.400' }}
                        />
                    )}
                </HStack>

                {chips.length > 0 && (
                    <HStack spacing={2} flexWrap="wrap">
                        {chips.map((chip, idx) => (
                            <Tag
                                key={`${chip.type}-${idx}`}
                                size="sm"
                                borderRadius="full"
                                colorScheme={
                                    chip.type === 'name'
                                        ? 'blue'
                                        : chip.type === 'position'
                                            ? 'green'
                                            : chip.type === 'minAb'
                                                ? 'purple'
                                                : 'orange'
                                }
                            >
                                <TagLabel>{chip.label}</TagLabel>
                                <TagCloseButton onClick={() => removeChip(chip)} />
                            </Tag>
                        ))}
                    </HStack>
                )}
            </VStack>

            {!leagueId && (
                <Text color="gray.400">Select a league to view stats.</Text>
            )}

            {leagueId && isLoading && (
                <Center py={8}>
                    <Spinner size="xl" />
                </Center>
            )}

            {leagueId && error && (
                <Text color="red.500" py={4}>
                    Error loading stats: {error instanceof Error ? error.message : 'Unknown error'}
                </Text>
            )}

            {leagueId && !isLoading && !error && rows.length === 0 && (
                <Text color="gray.400">No stats found.</Text>
            )}

            {leagueId && !isLoading && !error && rows.length > 0 && (
                <Box mt={4}>
                    <Text mb={2} color="white">
                        Showing {rows.length} players{typeof data?.total === 'number' ? ` (total ${data.total})` : ''}
                    </Text>
                    {statType === 'batting' ? (
                        <Box maxH="600px" overflowY="auto" borderRadius="md">
                            <Table variant="simple" size="sm" bg="whiteAlpha.100">
                                <Thead position="sticky" top={0} zIndex={1} bg="gray.800">
                                    <Tr>
                                        {renderSortableHeader('player', 'Player')}
                                        {renderSortableHeader('g', 'G', true)}
                                        {renderSortableHeader('pa', 'PA', true)}
                                        {renderSortableHeader('ab', 'AB', true)}
                                        {renderSortableHeader('runs', 'R', true)}
                                        {renderSortableHeader('hits', 'H', true)}
                                        {renderSortableHeader('hr', 'HR', true)}
                                        {renderSortableHeader('rbi', 'RBI', true)}
                                        {renderSortableHeader('bb', 'BB', true)}
                                        {renderSortableHeader('so', 'SO', true)}
                                        {renderSortableHeader('sb', 'SB', true)}
                                        {renderSortableHeader('bavg', 'AVG', true)}
                                        {renderSortableHeader('oba', 'OBP', true)}
                                        {renderSortableHeader('sa', 'SLG', true)}
                                        {renderSortableHeader('ops', 'OPS', true)}
                                        {renderSortableHeader('iso', 'ISO', true)}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {rows.map((row: any, index: number) => (
                                        <Tr
                                            key={row.id}
                                            bg={index % 2 === 0 ? 'whiteAlpha.100' : 'whiteAlpha.50'}
                                        >
                                            <Td color="white">
                                                <Text fontWeight="semibold">{getPlayerName(row)}</Text>
                                                <Text fontSize="xs" color="gray.300">
                                                    {getTeamLabel(row)}
                                                </Text>
                                            </Td>
                                            <Td color="white" isNumeric>{row.g}</Td>
                                            <Td color="white" isNumeric>{row.pa}</Td>
                                            <Td color="white" isNumeric>{row.ab}</Td>
                                            <Td color="white" isNumeric>{row.runs}</Td>
                                            <Td color="white" isNumeric>{row.hits}</Td>
                                            <Td color="white" isNumeric>{row.hr}</Td>
                                            <Td color="white" isNumeric>{row.rbi}</Td>
                                            <Td color="white" isNumeric>{row.bb}</Td>
                                            <Td color="white" isNumeric>{row.so}</Td>
                                            <Td color="white" isNumeric>{row.sb}</Td>
                                            <Td color="white" isNumeric>{Number(row.bavg).toFixed(3)}</Td>
                                            <Td color="white" isNumeric>{Number(row.oba).toFixed(3)}</Td>
                                            <Td color="white" isNumeric>{Number(row.sa).toFixed(3)}</Td>
                                            <Td color="white" isNumeric>{Number(row.ops).toFixed(3)}</Td>
                                            <Td color="white" isNumeric>{Number(row.iso).toFixed(3)}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    ) : (
                        <Box maxH="600px" overflowY="auto" borderRadius="md">
                            <Table variant="simple" size="sm" bg="whiteAlpha.100">
                                <Thead position="sticky" top={0} zIndex={1} bg="gray.800">
                                    <Tr>
                                        {renderSortableHeader('player', 'Player')}
                                        {renderSortableHeader('g', 'G', true)}
                                        {renderSortableHeader('gs', 'GS', true)}
                                        {renderSortableHeader('ip', 'IP', true)}
                                        {renderSortableHeader('bf', 'BF', true)}
                                        {renderSortableHeader('h', 'H', true)}
                                        {renderSortableHeader('r', 'R', true)}
                                        {renderSortableHeader('er', 'ER', true)}
                                        {renderSortableHeader('bb', 'BB', true)}
                                        {renderSortableHeader('so', 'SO', true)}
                                        {renderSortableHeader('hr', 'HR', true)}
                                        {renderSortableHeader('w', 'W', true)}
                                        {renderSortableHeader('l', 'L', true)}
                                        {renderSortableHeader('sv', 'SV', true)}
                                        {renderSortableHeader('era', 'ERA', true)}
                                        {renderSortableHeader('whip', 'WHIP', true)}
                                        {renderSortableHeader('bavg_against', 'BAVG', true)}
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {rows.map((row: any, index: number) => (
                                        <Tr
                                            key={row.id}
                                            bg={index % 2 === 0 ? 'whiteAlpha.100' : 'whiteAlpha.50'}
                                        >
                                            <Td color="white">
                                                <Text fontWeight="semibold">{getPlayerName(row)}</Text>
                                                <Text fontSize="xs" color="gray.300">
                                                    {getTeamLabel(row)}
                                                </Text>
                                            </Td>
                                            <Td color="white" isNumeric>{row.g}</Td>
                                            <Td color="white" isNumeric>{row.gs}</Td>
                                            <Td color="white" isNumeric>{Number(row.ip).toFixed(1)}</Td>
                                            <Td color="white" isNumeric>{row.bf}</Td>
                                            <Td color="white" isNumeric>{row.h}</Td>
                                            <Td color="white" isNumeric>{row.r}</Td>
                                            <Td color="white" isNumeric>{row.er}</Td>
                                            <Td color="white" isNumeric>{row.bb}</Td>
                                            <Td color="white" isNumeric>{row.so}</Td>
                                            <Td color="white" isNumeric>{row.hr}</Td>
                                            <Td color="white" isNumeric>{row.w}</Td>
                                            <Td color="white" isNumeric>{row.l}</Td>
                                            <Td color="white" isNumeric>{row.sv}</Td>
                                            <Td color="white" isNumeric>{Number(row.era).toFixed(2)}</Td>
                                            <Td color="white" isNumeric>{Number(row.whip).toFixed(2)}</Td>
                                            <Td color="white" isNumeric>{Number(row.bavg_against).toFixed(3)}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    )}
                </Box>
            )}
        </Container>
    );
};

export default StatisticsPage;

